import React from "react";

/**
 * ExclusiveIcon — premium animated badge replacing the old "bj" gold logo
 * for the Exclusive Games category. Crown silhouette with center diamond,
 * gold conic ring, soft halo and subtle sparkle. Pure CSS / SVG — no images.
 *
 * Sizes follow the parent: pass `size` (px) or rely on default 28.
 */
export default function ExclusiveIcon({
  size = 28,
  active = false,
  className = "",
}) {
  const s = size;
  return (
    <span
      aria-hidden
      className={`relative inline-flex items-center justify-center shrink-0 ${className}`}
      style={{ width: s, height: s }}
    >
      {/* Soft halo behind icon (only when active) */}
      {active && (
        <span
          aria-hidden
          className="absolute inset-[-6px] rounded-full pointer-events-none"
          style={{
            background:
              "radial-gradient(circle, rgba(251,191,36,0.55) 0%, rgba(251,191,36,0) 70%)",
            animation: "exIconHalo 2.4s ease-in-out infinite",
          }}
        />
      )}

      {/* Outer rotating gold conic ring */}
      <span
        aria-hidden
        className="absolute inset-0 rounded-full"
        style={{
          background:
            "conic-gradient(from 0deg, #fde68a, #f59e0b, #b45309, #f59e0b, #fde68a, #f59e0b, #b45309, #fde68a)",
          boxShadow: active
            ? "0 0 0 1.5px rgba(251,191,36,0.85), 0 6px 16px rgba(245,158,11,0.45), inset 0 1px 0 rgba(255,255,255,0.55)"
            : "0 0 0 1px rgba(251,191,36,0.55), inset 0 1px 0 rgba(255,255,255,0.4)",
          animation: active ? "exIconRotate 8s linear infinite" : "none",
        }}
      />

      {/* Glossy dark inner disc */}
      <span
        className="absolute inset-[2px] rounded-full overflow-hidden flex items-center justify-center"
        style={{
          background:
            "radial-gradient(circle at 30% 25%, #1f2937 0%, #0b1320 55%, #050912 100%)",
          boxShadow:
            "inset 0 -3px 6px rgba(0,0,0,0.6), inset 0 1px 2px rgba(255,255,255,0.15)",
        }}
      >
        {/* Top-left shine */}
        <span
          aria-hidden
          className="absolute -top-1 -left-1 rounded-full opacity-70 pointer-events-none"
          style={{
            width: s * 0.5,
            height: s * 0.5,
            background:
              "radial-gradient(circle, rgba(255,255,255,0.55) 0%, rgba(255,255,255,0) 70%)",
          }}
        />

        {/* Crown SVG — gold gradient */}
        <svg
          viewBox="0 0 24 24"
          width={s * 0.62}
          height={s * 0.62}
          className="relative drop-shadow-[0_1px_1px_rgba(0,0,0,0.6)]"
        >
          <defs>
            <linearGradient id="exGoldGrad" x1="0%" y1="0%" x2="0%" y2="100%">
              <stop offset="0%" stopColor="#fef3c7" />
              <stop offset="35%" stopColor="#fcd34d" />
              <stop offset="70%" stopColor="#f59e0b" />
              <stop offset="100%" stopColor="#b45309" />
            </linearGradient>
            <linearGradient id="exGemGrad" x1="0%" y1="0%" x2="0%" y2="100%">
              <stop offset="0%" stopColor="#fca5a5" />
              <stop offset="50%" stopColor="#ef4444" />
              <stop offset="100%" stopColor="#7f1d1d" />
            </linearGradient>
          </defs>
          {/* Crown body */}
          <path
            d="M3 8.5 L7 12 L12 5.5 L17 12 L21 8.5 L19.5 17 H4.5 Z"
            fill="url(#exGoldGrad)"
            stroke="#7c2d12"
            strokeWidth="0.6"
            strokeLinejoin="round"
          />
          {/* Base bar */}
          <rect
            x="4.2"
            y="17.4"
            width="15.6"
            height="2.4"
            rx="0.6"
            fill="url(#exGoldGrad)"
            stroke="#7c2d12"
            strokeWidth="0.6"
          />
          {/* Center red gem */}
          <circle cx="12" cy="13.6" r="1.55" fill="url(#exGemGrad)" />
          <circle cx="11.5" cy="13.1" r="0.45" fill="rgba(255,255,255,0.85)" />
          {/* Side dots */}
          <circle cx="6.2" cy="13.4" r="0.7" fill="#fff7d6" opacity="0.9" />
          <circle cx="17.8" cy="13.4" r="0.7" fill="#fff7d6" opacity="0.9" />
          {/* Tip jewels */}
          <circle cx="3" cy="8.5" r="0.85" fill="#fde68a" stroke="#7c2d12" strokeWidth="0.4" />
          <circle cx="12" cy="5.5" r="1" fill="#fde68a" stroke="#7c2d12" strokeWidth="0.4" />
          <circle cx="21" cy="8.5" r="0.85" fill="#fde68a" stroke="#7c2d12" strokeWidth="0.4" />
        </svg>

        {/* Sparkle (top-right, subtle twinkle) */}
        <span
          aria-hidden
          className="absolute"
          style={{
            top: s * 0.08,
            right: s * 0.1,
            width: s * 0.18,
            height: s * 0.18,
            background:
              "radial-gradient(circle, rgba(255,255,255,0.95) 0%, rgba(255,255,255,0) 70%)",
            animation: "exIconTwinkle 2.2s ease-in-out infinite",
          }}
        />
      </span>

      {/* Keyframes (scoped via global once — safe to render multiple times) */}
      <style>{`
        @keyframes exIconRotate { to { transform: rotate(360deg); } }
        @keyframes exIconHalo {
          0%,100% { opacity: 0.55; transform: scale(1); }
          50%     { opacity: 1;    transform: scale(1.08); }
        }
        @keyframes exIconTwinkle {
          0%,100% { opacity: 0.25; transform: scale(0.8); }
          50%     { opacity: 1;    transform: scale(1.15); }
        }
      `}</style>
    </span>
  );
}
